import React, { memo, useId } from "react";
import { Input, InputNumber, Switch } from "antd";
import type { InputProps } from "antd/es/input";
import type { InputNumberProps } from "antd/es/input-number";
import type { SwitchProps } from "antd/es/switch";
import { cn } from "src/util/cn";
import { useTranslation } from "react-i18next";

export type InputComponentType = "input" | "password" | "textarea" | "number" | "switch";

export interface CustomInputWithLabelProps {
  label?: React.ReactNode;
  showLabel?: boolean;
  labelPosition?: "top" | "left";
  inputComponent?: InputComponentType;
  showColon?: boolean;
  id?: string;
  /** Props for Input / Input.Password / Input.TextArea / InputNumber / Switch */
  inputProps?: InputProps | InputNumberProps | SwitchProps;
  className?: string;
  classNameLabel?: string;
  classNameInput?: string;
}

const CustomInputWithLabel: React.FC<CustomInputWithLabelProps> = ({
  label,
  showLabel = true,
  labelPosition = "top",
  inputComponent = "input",
  showColon = false,
  id: idProp,
  inputProps = {},
  className,
  classNameLabel,
  classNameInput,
}) => {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const { t } = useTranslation();

  const labelContent =
    typeof label === "string" ? t(label) : label;
  const labelEl =
    showLabel && labelContent != null ? (
      <label
        htmlFor={inputComponent === "switch" ? undefined : id}
        className={cn(
          "text-sm font-medium",
          "text-[var(--color-gray9)]",
          labelPosition === "left" ? "shrink-0" : "",
          classNameLabel
        )}
        style={{ color: "var(--color-gray9)" }}
      >
        {labelContent}
        {showColon ? ":" : ""}
      </label>
    ) : null;

  const inputEl = (() => {
    const base = { id: inputComponent !== "switch" ? id : undefined, className: cn(classNameInput, (inputProps as InputProps).className) };
    switch (inputComponent) {
      case "password":
        return <Input.Password {...base} {...(inputProps as InputProps)} />;
      case "textarea":
        return <Input.TextArea {...base} {...(inputProps as import("antd/es/input").TextAreaProps)} />;
      case "number":
        return <InputNumber {...(inputProps as InputNumberProps)} id={id} className={cn(classNameInput, (inputProps as InputNumberProps).className)} />;
      case "switch":
        return <Switch {...(inputProps as SwitchProps)} />;
      default:
        return <Input {...base} {...(inputProps as InputProps)} />;
    }
  })();

  return (
    <div
      className={cn(
        "flex gap-2",
        labelPosition === "top" ? "flex-col" : "flex-row items-center",
        className
      )}
    >
      {labelPosition === "top" ? (
        <>
          {labelEl}
          {inputEl}
        </>
      ) : (
        <>
          {labelEl}
          {inputEl}
        </>
      )}
    </div>
  );
};

export default memo(CustomInputWithLabel);
